import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';

import { Button } from '@project/components/atoms';
import { magazineService } from '@project/services';
import { PAYMENT_INTENT_SUCCESS, TOAST_DURATION, PAYMENT_PROCESSING_ERROR } from '@project/helper';

export const MagazineCheckoutForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setIsSubmitting(true);

    try {
      const result = await elements.submit();
      if (result.error) {
        toast.error(result.error.message || PAYMENT_PROCESSING_ERROR, TOAST_DURATION);
        return;
      }

      const { paymentIntent, error } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (paymentIntent && paymentIntent.status === PAYMENT_INTENT_SUCCESS) {
        // Activate the subscription server-side now instead of waiting on the webhook,
        // then refresh the magazine + subscription data so paid issues unlock immediately.
        await magazineService.confirmSubscription().catch(() => undefined);
        await Promise.all([
          queryClient.invalidateQueries(['getMagazines']),
          queryClient.invalidateQueries(['getMagazineSubscription']),
        ]);

        toast.success('You are now subscribed to magazines', TOAST_DURATION);
        navigate('/partner/magazines');
      } else {
        toast.error(error?.message || PAYMENT_PROCESSING_ERROR, TOAST_DURATION);
      }
    } catch (error) {
      toast.error(PAYMENT_PROCESSING_ERROR, TOAST_DURATION);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <PaymentElement />
      <Button
        type="submit"
        style="bg-gold text-white font-bold text-sm py-3 rounded px-3"
        isLoading={isSubmitting}
        isDisabled={!stripe || isSubmitting}
        onClick={handleSubmit}
      >
        Subscribe for $20 / month
      </Button>
    </form>
  );
};
